import classNames from "classnames";
import { UseSelectGetItemPropsOptions, UseSelectPropGetters } from "downshift";
import React from "react";
import { Text } from "../../text";
import { SelectProps } from "../select";
import { CSS_CLASSES } from "../select-css-classes";
import { SelectOptionsListItem, SelectOptionsListItemProps } from "../select-options-list-item";
import { isListOfOptions, isListOfOptionsGroups } from "../select-utils";
import { OptionsGroup, OptionsToString, SelectOption } from "../select.types";

export const selectOptionListSizes = {
  maxWidth: 320,
  maxHeight: 200,
};

export interface SelectOptionsListProps<S extends SelectOption> {
  options: S[] | OptionsGroup<S>[];
  highlightedIndex?: number;
  color?: SelectProps["color"];
  getItemProps: UseSelectPropGetters<S>["getItemProps"];
  optionsListItemRenderer?: (props: SelectOptionsListItemProps<S>) => JSX.Element;
  optionToString: OptionsToString<S>;
  clampWidth?: boolean;
  innerRef: React.Ref<HTMLUListElement>;
  style: React.CSSProperties;
  className?: string;
  useOptionIndexAsKey?: boolean;
}

export const SelectOptionsList = <S extends SelectOption = SelectOption>({
  options,
  highlightedIndex,
  color = "interactive",
  getItemProps,
  optionsListItemRenderer,
  optionToString,
  clampWidth,
  innerRef,
  style,
  className,
  useOptionIndexAsKey,
}: SelectOptionsListProps<S>) => {
  const widthStyles: React.CSSProperties = clampWidth ? selectOptionListSizes : {};
  return (
    <ul
      ref={innerRef}
      style={{ ...style, ...widthStyles }}
      className={classNames(CSS_CLASSES.SELECT_OPTIONS_LIST, className)}
      onMouseDown={(e) => {
        (e.nativeEvent as any).selectingOption = true;
      }}
    >
      {isListOfOptions(options) &&
        renderOptions(
          options as S[],
          optionToString,
          getItemProps,
          optionsListItemRenderer,
          highlightedIndex,
          color,
          useOptionIndexAsKey
        )}

      {isListOfOptionsGroups(options) &&
        renderOptionsGroups(
          options as OptionsGroup<S>[],
          optionToString,
          getItemProps,
          optionsListItemRenderer,
          highlightedIndex,
          color
        )}
    </ul>
  );
};

export const renderOptions = <S extends SelectOption>(
  options: SelectOption[],
  optionToString: OptionsToString<S>,
  getItemProps: UseSelectPropGetters<S>["getItemProps"],
  optionsListItemRenderer: (props: SelectOptionsListItemProps<S>) => JSX.Element,
  highlightedIndex: number,
  color: SelectProps["color"],
  useOptionIndexAsKey: boolean
) => {
  return options.map((option: S, index) => {
    const itemPropsGetterOptions = getItemPropGetterOptions(option, index);
    const itemProps = getItemProps(itemPropsGetterOptions);
    const key = useOptionIndexAsKey ? index : option.value;
    return (
      <li key={key} {...itemProps}>
        {optionsListItemRenderer ? (
          optionsListItemRenderer({ option, highlighted: index === highlightedIndex, optionToString, color })
        ) : (
          <SelectOptionsListItem
            option={option}
            highlighted={index === highlightedIndex}
            color={color}
            optionToString={optionToString}
          />
        )}
      </li>
    );
  });
};

export const renderOptionsGroups = <S extends SelectOption>(
  options: OptionsGroup<S>[],
  optionToString: OptionsToString<S>,
  getItemProps: UseSelectPropGetters<S>["getItemProps"],
  optionsListItemRenderer: (props: SelectOptionsListItemProps<S>) => JSX.Element,
  highlightedIndex: number,
  color: SelectProps["color"]
) => {
  return options.reduce(
    (result, group, groupIndex) => {
      result.groups.push(
        <li key={groupIndex}>
          <ul role="group" aria-labelledby={group.groupLabel}>
            <li role="presentation" id={group.groupLabel} className={CSS_CLASSES.SELECT_OPTION_GROUP_LABEL_ITEM}>
              <Text variant="label" color="currentcolor">
                {group.groupLabel}
              </Text>
            </li>
            {group.groupOptions.map((option) => {
              const index = result.itemIndex++;
              const itemPropsGetterOptions = getItemPropGetterOptions(option, index);
              const itemProps = getItemProps(itemPropsGetterOptions);
              return (
                <li key={option.value} {...itemProps}>
                  {optionsListItemRenderer ? (
                    optionsListItemRenderer({
                      option,
                      highlighted: index === highlightedIndex,
                      color,
                      optionToString,
                      isGroupOption: true,
                    })
                  ) : (
                    <SelectOptionsListItem
                      option={option}
                      highlighted={index === highlightedIndex}
                      color={color}
                      optionToString={optionToString}
                      isGroupOption
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </li>
      );

      return result;
    },
    { groups: [], itemIndex: 0 }
  ).groups;
};

const getItemPropGetterOptions = <S extends SelectOption>(option: S, index: number) => {
  const itemPropGetterOptions: UseSelectGetItemPropsOptions<S> = { item: option, index, disabled: option.disabled };
  if (option.id) {
    itemPropGetterOptions.id = option.id;
  }
  return itemPropGetterOptions;
};
